import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, ShoppingCart, CheckCircle, Edit, Trash2, XCircle, Users, CreditCard, ArrowRightLeft, LogOut, ClipboardList } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../services/api';
import ComandaSearchModal from '../components/ComandaSearchModal';
import BottomSheet from '../components/BottomSheet';
import { useBodyLock } from '../hooks/useBodyLock';
import { useMobileOptimizations } from '../hooks/useMobileOptimizations';
import { formatarMoeda } from '../utils/stringUtils';

interface Mesa {
  id: number;
  numero: string;
  nome: string;
  capacidade: number;
  status: string;
}

interface ComandaFisica {
  id: number;
  numero: string;
  nome: string;
  descricao?: string;
  ativo: boolean;
  uso_atual?: number;
}

interface ComandaMesa {
  id: number;
  mesa_id: number;
  comanda_fisica_id: number;
  status: string;
  total: number;
  mesa_numero: string;
  mesa_nome: string;
  comanda_numero: string;
  comanda_nome: string;
  garcom_nome: string;
  created_at: string;
  updated_at: string;
  itens_enviados_cozinha?: number;
  pode_deletar?: boolean;
}

const GarcomMesaPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isMobile } = useMobileOptimizations();

  const [mesa, setMesa] = useState<Mesa | null>(null);
  const [comandasMesa, setComandasMesa] = useState<ComandaMesa[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNovaComanda, setShowNovaComanda] = useState(false);
  const [showComandaModal, setShowComandaModal] = useState(false);
  const [tipoComanda, setTipoComanda] = useState<'sem' | 'com'>('sem');

  // Estados para transferência de mesa e comandas avulsas
  const [showModalTransferencia, setShowModalTransferencia] = useState(false);
  const [mesasDisponiveis, setMesasDisponiveis] = useState<Mesa[]>([]);
  const [loadingMesas, setLoadingMesas] = useState(false);
  const [transferindo, setTransferindo] = useState(false);
  const [showListagemAvulsas, setShowListagemAvulsas] = useState(false);
  const [comandasAvulsas, setComandasAvulsas] = useState<ComandaMesa[]>([]);
  const [loadingAvulsas, setLoadingAvulsas] = useState(false);

  // Bloquear scroll do body quando modais abertos no mobile
  useBodyLock(isMobile && (showComandaModal || showModalTransferencia || showNovaComanda || showListagemAvulsas));

  const isMountedRef = React.useRef(true);

  // Carregar dados da mesa
  const loadData = async () => {
    try {
      setLoading(true);
      const [mesaResponse, comandasResponse] = await Promise.all([
        api.get(`/api/garcom/mesas/${id}`),
        api.get(`/api/garcom/comandas?mesa_id=${id}`)
      ]);

      if (isMountedRef.current) {
        setMesa(mesaResponse.data);
        setComandasMesa(comandasResponse.data.comandas || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados da mesa:', error);
      if (isMountedRef.current) {
        toast.error('Erro ao carregar dados da mesa');
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    if (id) {
      loadData();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [id]);

  // Criar comanda sem comanda física
  const criarComandaSemFisica = async () => {
    try {
      await api.post('/api/garcom/comandas-mesa', {
        mesa_id: parseInt(id!)
      });

      toast.success('Comanda criada na mesa com sucesso!');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.erro || 'Erro ao criar comanda na mesa');
    } finally {
      setShowComandaModal(false);
    }
  };

  // Adicionar comanda física à mesa
  const adicionarComandaFisica = async (comandaFisica: ComandaFisica) => {
    try {
      await api.post('/api/garcom/comandas-mesa', {
        mesa_id: parseInt(id!),
        comanda_fisica_id: comandaFisica.id
      });

      toast.success('Comanda física adicionada à mesa com sucesso!');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.erro || 'Erro ao adicionar comanda física à mesa');
    } finally {
      setShowComandaModal(false);
      setShowNovaComanda(false);
    }
  };

  // Remover comanda da mesa
  const removerComanda = async (comandaId: number) => {
    if (!confirm('Tem certeza que deseja remover esta comanda da mesa?')) return;

    try {
      await api.delete(`/api/garcom/comandas-mesa/${comandaId}`);
      toast.success('Comanda removida da mesa!');
      loadData();
    } catch (error: any) {
      const errorMessage = error.response?.data?.erro || 'Erro ao remover comanda da mesa';
      toast.error(errorMessage);

      if (errorMessage.includes('enviados para cozinha')) {
        toast.error('⚠️ Não é possível remover comanda com itens enviados para cozinha!');
      }
    }
  };

  // Desanexar comanda da mesa (tornar avulsa)
  const desanexarComanda = async (comandaId: number) => {
    if (!confirm('Tem certeza que deseja desanexar esta comanda da mesa? Ela ficará como "Avulsa".')) return;

    try {
      await api.post(`/api/garcom/comandas/${comandaId}/desanexar-mesa`);
      toast.success('Comanda desanexada com sucesso!');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.erro || 'Erro ao desanexar comanda');
    }
  };

  // Carregar mesas disponíveis para transferência
  const carregarMesas = async () => {
    try {
      setLoadingMesas(true);
      const response = await api.get('/api/garcom/mesas');
      // Filtrar mesas livres (sem comandas ativas) e excluir a mesa atual
      const mesasLivres = response.data.filter((m: any) =>
        !m.comanda_id && m.id !== parseInt(id!)
      );
      setMesasDisponiveis(mesasLivres);
    } catch (error) {
      console.error('Erro ao carregar mesas:', error);
      toast.error('Erro ao carregar mesas disponíveis');
    } finally {
      setLoadingMesas(false);
    }
  };

  // Transferir todas as comandas da mesa para nova mesa
  const transferirMesa = async (novaMesaId: number) => {
    if (!mesa || comandasAtivas.length === 0) return;

    try {
      setTransferindo(true);
      await Promise.all(
        comandasAtivas.map(comanda =>
          api.post(`/api/garcom/comandas/${comanda.id}/transferir-mesa`, {
            novaMesaId
          })
        )
      );

      toast.success(`Mesa transferida com sucesso!`);
      navigate(`/garcom/mesa/${novaMesaId}`);
    } catch (error: any) {
      console.error('Erro ao transferir mesa:', error);
      toast.error(error.response?.data?.erro || 'Erro ao transferir mesa');
    } finally {
      setShowModalTransferencia(false);
      setTransferindo(false);
    }
  };

  // Carregar comandas avulsas (sem mesa)
  const carregarComandasAvulsas = async () => {
    try {
      setLoadingAvulsas(true);
      const response = await api.get('/api/garcom/comandas');
      // Filtrar apenas comandas ativas que NÃO têm mesa_id
      const avulsas = response.data.comandas.filter((c: any) => !c.mesa_id && c.status === 'ativa');
      setComandasAvulsas(avulsas);
    } catch (error) {
      console.error('Erro ao carregar comandas avulsas:', error);
      toast.error('Erro ao buscar comandas avulsas');
    } finally {
      setLoadingAvulsas(false);
    }
  };

  // Anexar uma comanda avulsa a esta mesa
  const anexarComandaAvulsa = async (comandaId: number) => {
    try {
      setTransferindo(true);
      await api.post(`/api/garcom/comandas/${comandaId}/transferir-mesa`, {
        novaMesaId: parseInt(id!)
      });

      toast.success('Comanda anexada à mesa com sucesso!');
      loadData();
    } catch (error: any) {
      toast.error(error.response?.data?.erro || 'Erro ao anexar comanda');
    } finally {
      setShowListagemAvulsas(false);
      setTransferindo(false);
    }
  };

  // Abrir modal de transferência
  const abrirModalTransferencia = async () => {
    if (comandasAtivas.length === 0) {
      toast.error('Não há comandas ativas para transferir');
      return;
    }
    await carregarMesas();
    setShowModalTransferencia(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Carregando mesa...</p>
        </div>
      </div>
    );
  }

  if (!mesa) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
        <div className="text-center bg-gray-800/80 border border-gray-700 p-6 rounded-2xl max-w-sm w-full shadow-xl">
          <h1 className="text-xl font-bold text-white mb-2">Mesa não encontrada</h1>
          <p className="text-gray-400 text-sm mb-6">Não foi possível carregar as informações desta mesa.</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => loadData()}
              className="w-full bg-orange-500 text-white py-3 rounded-xl font-bold hover:bg-orange-600 transition-all active:scale-95 shadow-lg shadow-orange-500/20"
            >
              Tentar Novamente
            </button>
            <button
              onClick={() => navigate('/garcom/home')}
              className="w-full bg-gray-700 text-gray-300 py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors"
            >
              Voltar ao Início
            </button>
          </div>
        </div>
      </div>
    );
  }

  const comandasAtivas = comandasMesa.filter(c => c.status === 'ativa');
  const comandasFechadas = comandasMesa.filter(c => c.status === 'fechada');
  const comandasPagas = comandasMesa.filter(c => c.status === 'paga');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 pb-24 lg:pb-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => navigate('/garcom/home')} className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors touch-manipulation active:scale-95 flex-shrink-0">
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </button>
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-white truncate leading-tight">{mesa.nome}</h1>
              <p className="text-sm sm:text-base text-gray-400 leading-normal">Capacidade: {mesa.capacidade} pessoas</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            {comandasAtivas.length > 0 && (
              <button onClick={abrirModalTransferencia} className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-4 py-2.5 rounded-lg font-bold hover:shadow-lg active:scale-95 flex items-center justify-center gap-2 min-h-[44px] text-sm sm:text-base">
                <ArrowRightLeft className="h-4 w-4" />
                <span>Transferir Mesa</span>
              </button>
            )}

            <button onClick={() => setShowComandaModal(true)} className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 sm:px-4 py-2.5 rounded-lg font-bold hover:shadow-lg active:scale-95 flex items-center justify-center gap-2 min-h-[44px] text-sm sm:text-base">
              <Plus className="h-4 w-4" />
              <span>Nova Comanda</span>
            </button>
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 rounded-lg p-3 sm:p-4 border border-green-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-300 text-xs sm:text-sm font-medium">Ativas</p>
                <p className="text-white text-xl sm:text-2xl font-bold">{comandasAtivas.length}</p>
              </div>
              <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-400" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-yellow-500/20 to-yellow-600/20 rounded-lg p-3 sm:p-4 border border-yellow-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-300 text-xs sm:text-sm font-medium">Fechadas</p>
                <p className="text-white text-xl sm:text-2xl font-bold">{comandasFechadas.length}</p>
              </div>
              <XCircle className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-400" />
            </div>
          </div>
          <div className="bg-gradient-to-r from-blue-500/20 to-blue-600/20 rounded-lg p-3 sm:p-4 border border-blue-500/30">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-300 text-xs sm:text-sm font-medium">Pagas</p>
                <p className="text-white text-xl sm:text-2xl font-bold">{comandasPagas.length}</p>
              </div>
              <ShoppingCart className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400" />
            </div>
          </div>
        </div>

        {/* Lista de Comandas */}
        {comandasAtivas.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-white font-bold text-lg">Comandas Ativas</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {comandasAtivas.map(comanda => (
                <div key={comanda.id} className="bg-gray-800/80 rounded-xl p-4 border border-green-500/30">
                  <h3 className="text-white font-bold">{comanda.comanda_nome}</h3>
                  <p className="text-gray-400 text-sm">#{comanda.comanda_numero}</p>
                  <p className="text-orange-400 font-bold mt-2">{formatarMoeda(comanda.total)}</p>
                  
                  <div className="flex flex-wrap gap-2 mt-4">
                    <button onClick={() => navigate(`/garcom/comanda/${comanda.id}`)} className="flex-1 min-w-[100px] bg-orange-500 text-white py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2">
                      <Edit className="w-4 h-4" /> Editar
                    </button>
                    <button onClick={() => desanexarComanda(comanda.id)} className="flex-1 min-w-[100px] bg-blue-500/20 text-blue-400 border border-blue-500/30 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2">
                      <LogOut className="w-4 h-4" /> Desanexar
                    </button>
                    <button onClick={() => removerComanda(comanda.id)} disabled={!!(comanda.itens_enviados_cozinha && comanda.itens_enviados_cozinha > 0)} className="flex-1 min-w-[100px] bg-red-500/10 text-red-400 border border-red-500/30 py-2 rounded-lg text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                      <Trash2 className="w-4 h-4" /> Remover
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {comandasMesa.length === 0 && (
          <div className="text-center py-20">
            <ShoppingCart className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma comanda nesta mesa</p>
          </div>
        )}
      </div>

      {/* Modais */}
      <BottomSheet isOpen={showComandaModal} onClose={() => setShowComandaModal(false)} title="Lançar Comanda">
        <div className="p-4 space-y-3">
          <button onClick={() => { setTipoComanda('sem'); criarComandaSemFisica(); }} className="w-full p-4 rounded-xl border border-green-500/30 bg-green-500/5 text-left flex items-center gap-4">
            <Users className="text-green-500" />
            <div>
              <p className="text-white font-bold">Sem Comanda Física</p>
              <p className="text-xs text-gray-400">Direto na mesa</p>
            </div>
          </button>
          <button onClick={() => { setTipoComanda('com'); setShowNovaComanda(true); setShowComandaModal(false); }} className="w-full p-4 rounded-xl border border-orange-500/30 bg-orange-500/5 text-left flex items-center gap-4">
            <CreditCard className="text-orange-500" />
            <div>
              <p className="text-white font-bold">Com Cartão/Física</p>
              <p className="text-xs text-gray-400">Usar comanda numerada</p>
            </div>
          </button>
          <button onClick={() => { carregarComandasAvulsas(); setShowListagemAvulsas(true); setShowComandaModal(false); }} className="w-full p-4 rounded-xl border border-blue-500/30 bg-blue-500/5 text-left flex items-center gap-4">
            <ClipboardList className="text-blue-500" />
            <div>
              <p className="text-white font-bold">Anexar Avulsa</p>
              <p className="text-xs text-gray-400">Puxar do balcão para cá</p>
            </div>
          </button>
        </div>
      </BottomSheet>

      {showNovaComanda && (
        <ComandaSearchModal isOpen={showNovaComanda} onClose={() => setShowNovaComanda(false)} onSelect={adicionarComandaFisica} title="Selecionar Comanda" description="Associe uma comanda física à mesa" excludeIds={[]} />
      )}

      <BottomSheet isOpen={showModalTransferencia} onClose={() => setShowModalTransferencia(false)} title="Transferir Mesa">
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-400">Transferir comandas ativas para:</p>
          {loadingMesas ? <div className="text-center py-10 animate-pulse text-gray-500">Carregando...</div> :
            mesasDisponiveis.length === 0 ? <p className="text-center py-10 text-gray-500">Sem mesas livres</p> :
            <div className="grid grid-cols-2 gap-2">
              {mesasDisponiveis.map(m => (
                <button key={m.id} onClick={() => transferirMesa(m.id)} disabled={transferindo} className="p-3 rounded-xl border border-blue-500/30 bg-blue-500/5 text-center transition-all active:scale-95">
                  <p className="text-white font-bold">{m.numero}</p>
                  <p className="text-[10px] text-gray-500">{m.nome}</p>
                </button>
              ))}
            </div>
          }
        </div>
      </BottomSheet>

      <BottomSheet isOpen={showListagemAvulsas} onClose={() => setShowListagemAvulsas(false)} title="Anexar Comanda Avulsa">
        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-400">Escolha uma comanda do balcão:</p>
          {loadingAvulsas ? <div className="text-center py-10 animate-pulse text-gray-500">Buscando...</div> :
            comandasAvulsas.length === 0 ? <p className="text-center py-10 text-gray-500">Sem comandas avulsas</p> :
            <div className="space-y-2">
              {comandasAvulsas.map(c => (
                <button key={c.id} onClick={() => anexarComandaAvulsa(c.id)} disabled={transferindo} className="w-full p-4 rounded-xl border border-orange-500/30 bg-orange-500/5 text-left flex justify-between items-center transition-all active:scale-98">
                  <div>
                    <p className="text-white font-bold">{c.comanda_nome}</p>
                    <p className="text-xs text-gray-500">#{c.comanda_numero} • {formatarMoeda(c.total)}</p>
                  </div>
                  <Plus className="text-orange-400" />
                </button>
              ))}
            </div>
          }
        </div>
      </BottomSheet>
    </div>
  );
};

export default GarcomMesaPage;
